import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Plus, Loader2, Layers, Power, RotateCcw, Lock, Users } from "lucide-react";
import { clearChaptersCache } from "@/hooks/useChapters";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ChapterEditor() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [data, setData] = useState({ active: [], inactive: [], member_counts: {} });
  const [newName, setNewName] = useState("");

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/chapters`, auth)
      .then((res) => setData(res.data))
      .catch(() => toast.error("Failed to load chapters"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const apply = (res) => {
    setData(res.data);
    clearChaptersCache();
  };

  const addChapter = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy("add");
    try {
      const res = await axios.post(`${BACKEND_URL}/api/chapters`, { name }, auth);
      apply(res);
      setNewName("");
      toast.success(`Chapter "${name}" added — it now appears in all chapter pickers`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add chapter");
    } finally { setBusy(""); }
  };

  const setStatus = async (name, active) => {
    if (!active && !window.confirm(`Deactivate "${name}"? It will be removed from all pickers and dropdowns. Its members and historical data remain intact, and reports will show it grayed out. You can reactivate it anytime.`)) return;
    setBusy(name);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/chapters/${encodeURIComponent(name)}/status`, { active }, auth);
      apply(res);
      toast.success(active ? `"${name}" reactivated — back in all pickers` : `"${name}" deactivated — hidden from pickers, history preserved`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setBusy(""); }
  };

  const ChapterRow = ({ name, inactive }) => (
    <div
      className={`flex items-center justify-between gap-3 px-4 py-3 rounded-lg border ${
        inactive ? "bg-slate-900/40 border-slate-700/60 opacity-60" : "bg-slate-900/60 border-slate-600"
      }`}
      data-testid={`chapter-row-${name}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className={`font-semibold truncate ${inactive ? "text-slate-400 line-through decoration-slate-500/60" : "text-white"}`}>
          {name}
        </span>
        <Badge variant="outline" className={`shrink-0 text-[10px] ${inactive ? "border-slate-600 text-slate-500" : "border-emerald-700 text-emerald-400"}`}>
          {inactive ? "Inactive" : "Active"}
        </Badge>
        <span className="flex items-center gap-1 text-xs text-slate-500 shrink-0">
          <Users className="w-3 h-3" /> {data.member_counts?.[name] ?? 0}
        </span>
      </div>
      {name === "National" ? (
        <span className="flex items-center gap-1 text-xs text-slate-500 shrink-0" data-testid="chapter-national-locked">
          <Lock className="w-3 h-3" /> Locked
        </span>
      ) : inactive ? (
        <Button size="sm" variant="outline" disabled={busy === name}
          onClick={() => setStatus(name, true)}
          className="border-emerald-700 text-emerald-400 hover:bg-emerald-900/30 shrink-0"
          data-testid={`chapter-activate-${name}`}>
          {busy === name ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RotateCcw className="w-3.5 h-3.5 mr-1" /> Reactivate</>}
        </Button>
      ) : (
        <Button size="sm" variant="outline" disabled={busy === name}
          onClick={() => setStatus(name, false)}
          className="border-red-800 text-red-400 hover:bg-red-900/30 shrink-0"
          data-testid={`chapter-deactivate-${name}`}>
          {busy === name ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Power className="w-3.5 h-3.5 mr-1" /> Deactivate</>}
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={() => navigate("/")}
            className="border-slate-600 text-slate-300 shrink-0" data-testid="chaptereditor-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
              <Layers className="w-5 h-5 text-amber-500" /> Chapter Editor
            </h1>
            <p className="text-slate-400 text-xs sm:text-sm">Add, deactivate, or reactivate chapters — no code changes needed</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <>
            <Card className="bg-slate-800/50 border-amber-800/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Plus className="w-4 h-4 text-amber-500" /> Add a New Chapter
                </CardTitle>
              </CardHeader>
              <CardContent className="flex gap-2">
                <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addChapter()}
                  placeholder="e.g. TX" maxLength={30}
                  className="bg-slate-700 border-slate-600 text-white" data-testid="chapter-add-input" />
                <Button onClick={addChapter} disabled={busy === "add" || !newName.trim()}
                  className="bg-amber-600 hover:bg-amber-700 text-white shrink-0" data-testid="chapter-add-btn">
                  {busy === "add" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Add</>}
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-base">Active Chapters</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2" data-testid="chapters-active-list">
                {data.active.map((c) => <ChapterRow key={c} name={c} inactive={false} />)}
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-300 text-base">Deactivated Chapters</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2" data-testid="chapters-inactive-list">
                {data.inactive.length === 0 ? (
                  <p className="text-slate-500 text-sm">No deactivated chapters.</p>
                ) : (
                  data.inactive.map((c) => <ChapterRow key={c} name={c} inactive />)
                )}
              </CardContent>
            </Card>

            <p className="text-slate-500 text-xs">
              Deactivating a chapter removes it from all dropdowns and pickers across the hub, but keeps all member records and history intact. Deactivated chapters still show grayed out in reports, exports, and other historical views.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
